// Simple License Service - ใช้ Local Storage (Singleton Pattern)
export class SimpleLicenseService {
  private static instance: SimpleLicenseService;
  private deviceFingerprint: string | null = null;
  private licenseKey = 'rebrand3d_device_license';
  private deviceSpecificKey: string | null = null;
  private initialized = false;

  private constructor() { }

  static getInstance(): SimpleLicenseService {
    if (!SimpleLicenseService.instance) {
      SimpleLicenseService.instance = new SimpleLicenseService();
    }
    return SimpleLicenseService.instance;
  }

  async initialize() {
    if (this.initialized) {
      console.log('License service already initialized');
      return this.getOrCreateLicense();
    }

    this.deviceFingerprint = await this.generateDeviceFingerprint();
    this.deviceSpecificKey = `${this.licenseKey}_${this.deviceFingerprint.substring(0, 16)}`;
    this.initialized = true;

    console.log('License service initialized');
    return this.getOrCreateLicense();
  }

  private async generateDeviceFingerprint(): Promise<string> {
    // ใช้เฉพาะข้อมูลที่ consistent ระหว่าง browser บนเครื่องเดียวกัน
    const deviceInfo = {
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      cpu: navigator.hardwareConcurrency?.toString() || 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // ลบ language ออก เพราะไม่ consistent ระหว่าง browser
      // language: navigator.language,
      // ใช้ hardware memory เป็น unique identifier แทน canvas
      memory: (navigator as any).deviceMemory || 'unknown',
      // เพิ่ม screen pixel ratio
      pixelRatio: window.devicePixelRatio || 1,
      userAgent: navigator.userAgent,
      platform: navigator.platform,

      // ✨ เพิ่ม Canvas fingerprinting เพื่อเพิ่ม uniqueness
      canvas: await this.getCanvasFingerprint()
    };

    // 🔍 Log device specs สำหรับ debug
    console.log('🔍 Device Specs for License Generation:', {
      screen: deviceInfo.screen,
      cpu: deviceInfo.cpu,
      timezone: deviceInfo.timezone,
      // language: navigator.language, // Log แต่ไม่ใช้ใน fingerprint
      memory: deviceInfo.memory,
      pixelRatio: deviceInfo.pixelRatio,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      timestamp: new Date().toISOString(),
      note: 'Language excluded from fingerprint for consistency'
    });

    const combined = JSON.stringify(deviceInfo);
    const encoder = new TextEncoder();
    const data = encoder.encode(combined);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    const fingerprint = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    // 🔍 Log final fingerprint
    console.log('🔍 Generated Device Fingerprint:', {
      input: deviceInfo,
      combined: combined,
      fingerprint: fingerprint,
      fingerprintShort: fingerprint.substring(0, 16) + '...'
    });

    return fingerprint;
  }

  private async getCanvasFingerprint(): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 100;
    const ctx = canvas.getContext('2d')!;

    // Text rendering (ขึ้นกับ system fonts + OS rendering)
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#1f2937';
    ctx.fillText('Device fingerprint test 🔒 123', 2, 2);

    // Gradient rendering (ขึ้นกับ GPU rendering)
    const gradient = ctx.createLinearGradient(0, 20, 200, 20);
    gradient.addColorStop(0, 'rgba(255,0,0,0.8)');
    gradient.addColorStop(0.5, 'rgba(0,255,0,0.8)');
    gradient.addColorStop(1, 'rgba(0,0,255,0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 20, 200, 30);

    // Shape with transparency (ขึ้นกับ compositing algorithms)
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = 'rgba(255,0,255,0.5)';
    ctx.beginPath();
    ctx.arc(100, 60, 20, 0, Math.PI * 2);
    ctx.fill();

    // Mathematical curve (ขึ้นกับ precision ของ hardware)
    ctx.strokeStyle = '#059669';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 100; i++) {
      const x = i * 2;
      const y = 80 + Math.sin(i * 0.1) * 10;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const dataURL = canvas.toDataURL();
    // สร้าง hash เพื่อลดขนาด
    const encoder = new TextEncoder();
    const data = encoder.encode(dataURL);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
  }

  private getOrCreateLicense() {
    // ใช้ device-specific key
    const storageKey = this.deviceSpecificKey || this.licenseKey;
    const existingLicense = localStorage.getItem(storageKey);

    if (existingLicense) {
      try {
        const license = JSON.parse(existingLicense);

        // เช็คว่า device fingerprint ตรงกันหรือไม่
        if (license.deviceFingerprint === this.deviceFingerprint) {
          console.log('📱 Found existing license:', {
            device: license.deviceFingerprint?.substring(0, 16) + '...',
            licenseId: license.licenseId,
            created: license.createdAt
          });
          return license;
        } else {
          console.warn('Device fingerprint mismatch, creating new license');
        }
      } catch (error) {
        console.warn('Invalid license format, creating new one');
      }
    }

    // สร้าง license ID จาก device fingerprint
    const licenseId = this.generateLicenseIdFromFingerprint();

    // สร้างใหม่ - ไม่เก็บ quota ข้อมูล เพราะต้องถาม server
    const newLicense = {
      deviceFingerprint: this.deviceFingerprint,
      licenseId: licenseId,
      createdAt: new Date().toISOString(),
      version: '1.0'
    };

    localStorage.setItem(storageKey, JSON.stringify(newLicense));

    console.log('✨ Created new license:', {
      licenseId: newLicense.licenseId,
      device: newLicense.deviceFingerprint?.substring(0, 16) + '...'
    });

    return newLicense;
  }

  // สร้าง license ID จาก device fingerprint เพื่อให้ consistent
  private generateLicenseIdFromFingerprint(): string {
    if (!this.deviceFingerprint) {
      throw new Error('Device fingerprint not initialized');
    }

    // ใช้ device fingerprint เพื่อสร้าง license ID ที่ consistent
    const shortFingerprint = this.deviceFingerprint.substring(0, 12);
    return `lic_${shortFingerprint}`;
  }

  canGenerate(): boolean {
    // Frontend ไม่ควรตัดสินใจเรื่อง quota - ต้องถาม server
    console.warn('canGenerate() should check with server, not frontend');
    return true; // ให้ผ่านไปก่อน แล้วให้ server ตัดสินใจ
  }

  getLicenseInfo() {
    if (!this.deviceFingerprint) {
      throw new Error('SimpleLicenseService not initialized. Call initialize() first.');
    }
    return this.getOrCreateLicense();
  }

  // Method สำหรับ get license key ที่จะส่งไป server
  getLicenseKey(): string | null {
    if (!this.deviceFingerprint) {
      console.warn('SimpleLicenseService not initialized. Call initialize() first.');
      return null;
    }
    const license = this.getOrCreateLicense();
    return license.licenseId || null;
  }

  // สำหรับส่งไป server validation (ถ้าต้องการ)
  getLicenseForServerValidation() {
    if (!this.deviceFingerprint) {
      throw new Error('SimpleLicenseService not initialized. Call initialize() first.');
    }
    const license = this.getOrCreateLicense();
    return {
      licenseId: license.licenseId,
      deviceFingerprint: license.deviceFingerprint,
      createdAt: license.createdAt,
      version: license.version
    };
  }

  // Reset license (สำหรับ debug)
  resetLicense() {
    const storageKey = this.deviceSpecificKey || this.licenseKey;
    localStorage.removeItem(storageKey);
    console.log('🔄 License reset');
  }

  // ส่ง license information ไปยัง server
  async sendLicenseToServer(apiUrl: string) {
    const license = this.getOrCreateLicense();

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': license.licenseId
        },
        body: JSON.stringify({
          licenseId: license.licenseId,
          deviceFingerprint: license.deviceFingerprint,
          createdAt: license.createdAt,
          version: license.version
        })
      });

      const result = await response.json();
      console.log('✅ License sent to server:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to send license to server:', error);
      throw error;
    }
  }
}
